import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import LandingNav, { LANDING_NAV_HEIGHT } from '../components/LandingNav'
import LandingFooter from '../components/LandingFooter'
import SeoHead from '../components/SeoHead'
import MatchReportShowcase from '../components/MatchReportShowcase'

const MotionLink = motion.create(Link)

/* ─── Palette ────────────────────────────────── */
const C = {
  ink:   '#0A0A0A',
  ink2:  '#111111',
  ink3:  '#1A1A1A',
  rule:  '#2A2A2A',
  muted: '#5a5650',
  sub:   '#888888',
  cream: '#FFFFFF',
  gold:     '#4F8EF7',
  goldDim:  'rgba(79,142,247,0.2)',
  goldSoft: 'rgba(79,142,247,0.08)',
  bg:    '#FFFFFF',
  bgAlt: '#F7F7F7',
  text:  '#0A0A0A',
  textMuted: '#888888',
  textSub: '#AAAAAA',
  border: '#EBEBEB',
  accent: '#4F8EF7',
  display: "'Barlow Condensed', sans-serif",
  body:    "'Space Grotesk', sans-serif",
  mono:    "'Space Grotesk', sans-serif",
}

const CLUBS = [
  { name: 'Gazelec Ajaccio', logo: '/logos-clubs/gazelec-ajaccio.png' },
  { name: 'US Castanet',     logo: '/logos-clubs/us-castanet.png' },
  { name: 'JS Cugnaux',      logo: '/logos-clubs/js-cugnaux.png' },
  { name: 'AS Hersoise',     logo: '/logos-clubs/as-hersoise.png' },
]

const GLOBAL_CSS = `
::selection { background: rgba(79,142,247,0.25); color: #0A0A0A; }
html { scroll-behavior: smooth; }
html, body { overflow-x: clip; }

@keyframes ib-marquee {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}
.ib-marquee-wrap:hover .ib-marquee-track {
  animation-play-state: paused;
}
@media (prefers-reduced-motion: reduce) {
  .ib-marquee-track, .ib-kb-img { animation: none !important; }
}
@keyframes ib-kb { 0% { transform: scale(1); } 100% { transform: scale(1.03); } }
.ib-kb-img { animation: ib-kb 20s ease-in-out infinite alternate; }
@media (max-width: 900px) {
  .ib-contact-grid { grid-template-columns: 1fr !important; }
}
`

const VIEWPORT = { once: true, amount: 0.2 }

const fadeUp = {
  hidden: { opacity: 0, y: 34 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
}
const stagger = (delay = 0.2, delayChildren = 0) => ({
  hidden: {},
  show: { transition: { staggerChildren: delay, delayChildren } },
})

const eyebrowStyle = {
  fontFamily: C.mono, fontSize: 9, fontWeight: 600, color: C.gold,
  letterSpacing: '0.2em', textTransform: 'uppercase',
}

function CountUp({ to, suffix = '', duration = 2, format, style }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, amount: 0.5 })
  const [val, setVal] = useState(0)
  useEffect(() => {
    if (!inView) return
    const start = performance.now()
    let raf
    const tick = (now) => {
      const p = Math.min(1, (now - start) / (duration * 1000))
      const eased = 1 - Math.pow(1 - p, 3)
      setVal(Math.round(to * eased))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [inView, to, duration])
  return (
    <span ref={ref} style={style}>
      {(format ? format(val) : String(val)) + suffix}
    </span>
  )
}

const frFormat = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

function FieldWrap({ label, focused, children }) {
  return (
    <div style={{ borderBottom: `1.5px solid ${focused ? C.accent : '#D1D5DB'}`, marginBottom: 22, paddingBottom: 2, transition: 'border-color .15s' }}>
      <label style={{ display: 'block', fontFamily: C.mono, fontSize: 9, fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: focused ? C.accent : '#6B7280', marginBottom: 6, transition: 'color .15s' }}>
        {label}
      </label>
      {children}
    </div>
  )
}

const DEMO_INPUT_ST = {
  width: '100%', background: 'transparent', border: 'none', outline: 'none',
  fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, color: '#0A0A0A',
  padding: '2px 0 6px', lineHeight: 1.4,
}

export default function Home() {
  const [lang, setLang] = useState('FR')
  const toggleLang = () => setLang((l) => (l === 'FR' ? 'EN' : 'FR'))

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  )
  useEffect(() => {
    const ro = new ResizeObserver(() => setIsMobile(window.innerWidth < 768))
    ro.observe(document.documentElement)
    return () => ro.disconnect()
  }, [])

  useEffect(() => { window.scrollTo(0, 0) }, [])

  const statRef = useRef(null)
  const statInView = useInView(statRef, { once: true, amount: 0.2 })

  const [photoVisible, setPhotoVisible] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setPhotoVisible(true), 150)
    return () => clearTimeout(t)
  }, [])

  const [demoForm, setDemoForm] = useState({ prenom: '', nom: '', email: '', telephone: '', club: '', role: '', niveau: '', message: '' })
  const [demoSent, setDemoSent] = useState(false)
  const [demoLoading, setDemoLoading] = useState(false)
  const [demoFocused, setDemoFocused] = useState(null)

  const handleDemoSubmit = async (e) => {
    e.preventDefault()
    setDemoLoading(true)
    const payload = {
      sheet:     'Contact',
      prenom:    demoForm.prenom,
      nom:       demoForm.nom,
      email:     demoForm.email,
      telephone: demoForm.telephone,
      club:      demoForm.club,
      role:      demoForm.role,
      niveau:    demoForm.niveau,
      message:   demoForm.message,
      date:      new Date().toISOString(),
    }
    console.log('[Insightball] Soumission formulaire démo →', payload)
    try {
      await fetch(import.meta.env.VITE_SHEETS_URL, {
        method: 'POST',
        mode:   'no-cors',
        headers: { 'Content-Type': 'text/plain' },
        body:    JSON.stringify(payload),
      })
      console.log('[Insightball] fetch envoyé (no-cors, réponse opaque)')
    } catch (err) {
      console.error('[Insightball] Erreur fetch :', err)
    }
    setDemoSent(true)
    setDemoLoading(false)
  }

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: C.body }}>
      <SeoHead
        title="Insightball · Rapport de match et stats football"
        description="Rapport d'analyse tactique complet à partir d'une vidéo smartphone. Stats collectives et suivi de progression sur toute la saison."
        canonical="https://insightball.com/"
        ogTitle="Insightball · Rapport de match et stats football"
        ogDescription="Rapport d'analyse tactique complet à partir d'une vidéo smartphone. Stats collectives et suivi de progression sur toute la saison."
      />
      <style>{GLOBAL_CSS}</style>
      <LandingNav lang={lang} onToggleLang={toggleLang} />

      {/* ═══ SECTION 1 · HERO ═══ */}
      <section style={{
        minHeight: '100vh', boxSizing: 'border-box',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        paddingTop: LANDING_NAV_HEIGHT,
        background: isMobile
          ? '#FFFFFF'
          : 'linear-gradient(to right, #FFFFFF 50%, #F7F7F7 50%)',
      }}>
        {/* Texte */}
        <div style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: isMobile ? '48px 24px 32px' : '80px 60px',
        }}>
          <h1 style={{
            fontFamily: C.display, fontSize: isMobile ? 38 : 54, fontWeight: 900,
            lineHeight: 0.92, textTransform: 'uppercase', color: C.text, margin: '0 0 20px',
          }}>
            Tout part d'une vidéo.<br />
            <span style={{ color: C.accent }}>Analyse. Rapport. Décisions.</span>
          </h1>
          <p style={{
            fontFamily: C.body, fontSize: 15, color: C.textMuted, lineHeight: 1.7,
            margin: '0 0 32px', maxWidth: 420,
          }}>
            Uploade ta vidéo. Récupère un rapport prêt à partager avec ton staff.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/rapport-42" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: '#0A0A0A', color: '#FFFFFF', fontFamily: C.mono, fontSize: 13, fontWeight: 600,
              textDecoration: 'none', padding: '0 28px', minHeight: 52, borderRadius: 3,
              width: isMobile ? '100%' : 'fit-content', justifyContent: 'center',
            }}>
              Voir un rapport de match →
            </Link>
            <a href="/#contact" style={{
              display: 'inline-flex', alignItems: 'center',
              background: 'transparent', color: '#0A0A0A', fontFamily: C.mono, fontSize: 13, fontWeight: 600,
              textDecoration: 'none', padding: '0 28px', minHeight: 52, borderRadius: 3,
              border: '1.5px solid #0A0A0A',
              width: isMobile ? '100%' : 'fit-content', justifyContent: 'center',
            }}>
              Demander une démo
            </a>
          </div>
          <p style={{ fontFamily: C.body, fontSize: 12, color: C.textSub, marginTop: 14 }}>
            Analyse tactique · Stats collectives · Suivi de saison
          </p>
        </div>

        {/* Photo National 2 — ratio natif, Ken Burns, fade-in */}
        <div style={{
          background: isMobile ? C.bgAlt : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: isMobile ? '24px' : '60px 48px',
        }}>
          <div style={{
            width: '100%',
            aspectRatio: '1600 / 955',
            borderRadius: isMobile ? 12 : 16,
            overflow: 'hidden',
            boxShadow: '0 20px 60px -20px rgba(0,0,0,0.25)',
            opacity: photoVisible ? 1 : 0,
            transform: photoVisible ? 'translateX(0)' : 'translateX(30px)',
            transition: 'opacity .6s ease-out .15s, transform .6s ease-out .15s',
          }}>
            <picture>
              <source srcSet="/hero-terrain-national2.webp" type="image/webp" />
              <img
                src="/hero-terrain-national2.jpg"
                alt="Match National 2 — tribunes, banc de touche, caméra sur trépied"
                className="ib-kb-img"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </picture>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 1b · PREUVE SOCIALE MARQUEE ═══ */}
      <section
        aria-label="Clubs utilisant Insightball"
        style={{ background: '#FFFFFF', borderTop: `0.5px solid ${C.border}`, borderBottom: `0.5px solid ${C.border}`, padding: isMobile ? '32px 0' : '44px 0' }}
      >
        <p style={{
          textAlign: 'center',
          fontFamily: C.mono, fontSize: 10, fontWeight: 500,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: C.textSub, marginBottom: isMobile ? 24 : 32,
          paddingLeft: 24, paddingRight: 24,
        }}>
          Ils utilisent Insightball
        </p>

        {/* Marquee wrapper — overflow hidden + masque fondu sur les bords */}
        <div
          className="ib-marquee-wrap"
          style={{
            overflow: 'hidden',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
            maskImage:        'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
          }}
        >
          {/* Track dupliqué × 2 : translateX(-50%) = exactement 1 jeu de logos → boucle seamless */}
          <div
            className="ib-marquee-track"
            style={{
              display: 'flex',
              alignItems: 'center',
              width: 'max-content',
              animation: 'ib-marquee 28s linear infinite',
            }}
          >
            {[...CLUBS, ...CLUBS].map((club, i) => (
              <div
                key={i}
                style={{
                  flexShrink: 0,
                  padding: isMobile ? '0 30px' : '0 56px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <img
                  src={club.logo}
                  alt={club.name}
                  loading="eager"
                  style={{
                    height: isMobile ? 52 : 72,
                    width: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2 · STAT CHOC ═══ */}
      <section ref={statRef} style={{ background: C.bg, padding: '100px 24px', borderBottom: `0.5px solid ${C.border}` }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={statInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.4 }}
            style={{
              fontFamily: C.display, fontSize: isMobile ? 72 : 120, fontWeight: 900, lineHeight: 1,
              color: C.text, letterSpacing: '0.02em',
            }}
          >
            <CountUp to={30000} duration={2} format={frFormat} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={statInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.7 }}
            style={{ fontFamily: C.body, fontSize: 20, color: C.text, marginTop: 16 }}
          >
            matchs joués chaque week-end en France.
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={statInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 1.3 }}
            style={{ fontFamily: C.body, fontSize: 15, color: C.textMuted, marginTop: 12 }}
          >
            La majorité ne génère aucune donnée exploitable.
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={statInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 1.9, ease: 'easeOut' }}
            style={{ fontFamily: C.body, fontSize: 15, color: C.gold, marginTop: 6, fontWeight: 600 }}
          >
            Insightball change ça.
          </motion.div>
        </div>
      </section>

      <MatchReportShowcase />

      {/* ═══ SECTION 4 · COMMENT ÇA MARCHE ═══ */}
      <section style={{ background: C.bg, padding: '96px 24px', borderBottom: `0.5px solid ${C.border}` }}>
        <div style={{ maxWidth: 960, margin: '0 auto', textAlign: 'center' }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={VIEWPORT}>
            <h2 style={{
              fontFamily: C.display, fontSize: isMobile ? 30 : 40, fontWeight: 800, textTransform: 'uppercase',
              color: C.text, margin: '0 0 12px', lineHeight: 1.0,
            }}>
              Simple comme un coup de sifflet.
            </h2>
            <p style={{ fontFamily: C.body, fontSize: 15, color: C.textMuted, margin: '0 0 56px' }}>
              Pas besoin de matériel pro. Votre téléphone suffit.
            </p>
          </motion.div>

          <motion.div
            variants={stagger(0.2)}
            initial="hidden"
            whileInView="show"
            viewport={VIEWPORT}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1px 1fr 1px 1fr',
              gap: isMobile ? 40 : 0, textAlign: 'left',
            }}
          >
            {[
              { n: '01', t: 'Filmez',  d: 'Téléphone ou caméra.' },
              { n: '02', t: 'Envoyez', d: 'Upload depuis votre dashboard.' },
              { n: '03', t: 'Recevez', d: 'Rapport d\'analyse complet.' },
            ].flatMap((step, i) => {
              const block = (
                <motion.div
                  key={step.n}
                  variants={{
                    hidden: { opacity: 0, y: 40 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
                  }}
                  style={{ padding: isMobile ? 0 : '0 36px' }}
                >
                  <div style={{ position: 'relative', paddingTop: 32 }}>
                    <span style={{
                      position: 'absolute', top: -18, left: -8,
                      fontFamily: C.display, fontSize: 100, fontWeight: 900, color: '#4F8EF7', opacity: 0.12,
                      lineHeight: 1, pointerEvents: 'none',
                    }}>
                      {step.n}
                    </span>
                    <div style={{ fontFamily: C.mono, fontSize: 9, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#4F8EF7', marginBottom: 10, position: 'relative' }}>
                      {step.n}
                    </div>
                    <h3 style={{
                      fontFamily: C.display, fontSize: 24, fontWeight: 800, textTransform: 'uppercase',
                      color: C.text, margin: '0 0 10px', position: 'relative',
                    }}>
                      {step.t}
                    </h3>
                    <p style={{ fontFamily: C.body, fontSize: 14, color: C.textMuted, lineHeight: 1.6, margin: 0, position: 'relative' }}>
                      {step.d}
                    </p>
                  </div>
                </motion.div>
              )
              return i < 2 && !isMobile
                ? [block, <div key={`sep-${i}`} style={{ background: C.border, width: 1 }} />]
                : [block]
            })}
          </motion.div>
        </div>
      </section>

      {/* ═══ SECTION 5 · CTA FINAL ═══ */}
      <section style={{ position: 'relative', overflow: 'hidden', background: C.ink }}>
        <img
          src="/images/terrain.jpg"
          alt=""
          aria-hidden="true"
          loading="lazy"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.15 }}
        />
        <motion.div
          variants={stagger(0.15)}
          initial="hidden"
          whileInView="show"
          viewport={VIEWPORT}
          style={{
            position: 'relative', zIndex: 2, padding: isMobile ? '80px 24px' : '140px 24px',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', textAlign: 'center',
          }}
        >
          <motion.h2
            variants={fadeUp}
            style={{
              fontFamily: C.display, fontSize: isMobile ? 44 : 72, fontWeight: 800, lineHeight: 1.0,
              textTransform: 'uppercase', color: C.cream, margin: '0 0 36px',
            }}
          >
            Passez<br />à <span style={{ color: C.accent }}>l'action.</span>
          </motion.h2>
          <motion.div variants={fadeUp} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <motion.a
              href="/#contact"
              whileHover={{ opacity: 0.85 }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                background: '#4F8EF7', color: '#FFFFFF',
                fontFamily: C.mono, fontSize: 13, fontWeight: 700,
                textDecoration: 'none', padding: '0 40px', minHeight: 56, borderRadius: 3,
              }}
            >
              Demander une démo →
            </motion.a>
            <MotionLink
              to="/signup"
              whileHover={{ opacity: 0.85 }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                background: 'transparent', color: '#FFFFFF',
                fontFamily: C.mono, fontSize: 13, fontWeight: 700,
                textDecoration: 'none', padding: '0 40px', minHeight: 56, borderRadius: 3,
                border: '1.5px solid rgba(255,255,255,0.4)',
              }}
            >
              Créer un compte
            </MotionLink>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══ SECTION COMMUNAUTÉ WHATSAPP ═══ */}
      <section style={{
        background: '#FFFFFF',
        padding: isMobile ? '72px 24px' : '100px 60px',
        borderTop: `0.5px solid ${C.border}`,
        borderBottom: `0.5px solid ${C.border}`,
      }}>
        <motion.div
          variants={stagger(0.15)}
          initial="hidden"
          whileInView="show"
          viewport={VIEWPORT}
          style={{
            maxWidth: 1100,
            margin: '0 auto',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: isMobile ? 48 : 80,
          }}
        >
          {/* ── Photo (droite desktop / haut mobile) ── */}
          <motion.div
            variants={{ hidden: { opacity: 0, x: 30 }, show: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut', delay: 0.15 } } }}
            style={{ flex: '0 0 45%', order: isMobile ? -1 : 0 }}
          >
            <img
              src="/communaute-staffs.webp"
              alt="Staffs techniques qui analysent un match avec Insightball"
              loading="lazy"
              style={{
                width: '100%',
                height: isMobile ? 240 : 420,
                objectFit: 'cover',
                borderRadius: 16,
                boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
                display: 'block',
              }}
            />
          </motion.div>

          {/* ── Texte (gauche desktop / bas mobile) ── */}
          <div style={{ flex: '1 1 55%' }}>
            <motion.div
              variants={fadeUp}
              style={{
                fontFamily: C.mono, fontSize: 9, fontWeight: 600,
                letterSpacing: '0.2em', textTransform: 'uppercase',
                color: C.accent, marginBottom: 20,
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <span style={{ width: 18, height: 1, background: C.accent, display: 'inline-block' }} />
              La communaute
              <span style={{ width: 18, height: 1, background: C.accent, display: 'inline-block' }} />
            </motion.div>

            <motion.h2
              variants={fadeUp}
              style={{
                fontFamily: C.display,
                fontSize: isMobile ? 30 : 44,
                fontWeight: 800,
                lineHeight: 1.05,
                textTransform: 'uppercase',
                color: C.text,
                margin: '0 0 20px',
              }}
            >
              Rejoins les staffs qui construisent Insightball avec nous.
            </motion.h2>

            <motion.p
              variants={fadeUp}
              style={{
                fontFamily: C.body,
                fontSize: 15,
                color: C.textMuted,
                lineHeight: 1.7,
                margin: '0 0 36px',
                maxWidth: 440,
              }}
            >
              Echanges sur l'analyse de match, retours terrain, nouveautes en avant-premiere.
              Pas de spam, juste des coachs qui parlent foot.
            </motion.p>

            <motion.a
              variants={fadeUp}
              href="https://chat.whatsapp.com/KRz1ZyXKlvFLxb90GtaM1N"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                background: '#25D366', color: '#FFFFFF',
                fontFamily: C.mono, fontSize: 13, fontWeight: 700,
                textDecoration: 'none', padding: '0 36px', minHeight: 54, borderRadius: 3,
                width: isMobile ? '100%' : 'auto',
                letterSpacing: '0.02em',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              Rejoindre le groupe
            </motion.a>
          </div>
        </motion.div>
      </section>

      {/* ═══ SECTION CONTACT ═══ */}
      <section id="contact" style={{ background: '#f5f5f5', padding: isMobile ? '72px 24px' : '100px 60px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div
            className="ib-contact-grid"
            style={{ display: 'grid', gridTemplateColumns: '2fr 3fr', gap: 64, alignItems: 'start' }}
          >
            {/* ── Colonne gauche : texte + carte info ── */}
            <motion.div variants={stagger(0.15)} initial="hidden" whileInView="show" viewport={VIEWPORT}>
              <motion.div variants={fadeUp} style={{ fontFamily: C.mono, fontSize: 9, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.accent, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 18, height: 1, background: C.accent, display: 'inline-block' }} />
                Nous contacter
              </motion.div>
              <motion.h2
                variants={fadeUp}
                style={{ fontFamily: C.display, fontSize: isMobile ? 34 : 46, fontWeight: 900, textTransform: 'uppercase', lineHeight: 0.95, color: C.text, margin: '0 0 20px' }}
              >
                Une question,<br />une demande,<br />un projet ?
              </motion.h2>
              <motion.p
                variants={fadeUp}
                style={{ fontFamily: C.body, fontSize: 15, color: C.textMuted, lineHeight: 1.7, maxWidth: 340, margin: '0 0 32px' }}
              >
                Renseignement, démonstration produit, partenariat ou simple échange sur ton club, écris-nous.
              </motion.p>

              {/* Carte info */}
              <motion.div
                variants={fadeUp}
                style={{ background: '#ffffff', border: '0.5px solid #e5e5e5', borderRadius: 12, padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}
              >
                {/* Email */}
                <a href="mailto:contact@insightball.com" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
                  <span style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: C.goldSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <polyline points="2,4 12,13 22,4" />
                    </svg>
                  </span>
                  <span style={{ fontFamily: C.body, fontSize: 13, color: C.accent, fontWeight: 600 }}>contact@insightball.com</span>
                </a>

                {/* Réponse */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12,6 12,12 16,14" />
                    </svg>
                  </span>
                  <span style={{ fontFamily: C.body, fontSize: 13, color: C.textMuted }}>Réponse sous 24h</span>
                </div>

                {/* France */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="18" height="13" viewBox="0 0 18 13" aria-hidden="true">
                      <rect width="6" height="13" fill="#002395" />
                      <rect x="6" width="6" height="13" fill="#EDEDED" />
                      <rect x="12" width="6" height="13" fill="#ED2939" />
                    </svg>
                  </span>
                  <span style={{ fontFamily: C.body, fontSize: 13, color: C.textMuted }}>Basé en France</span>
                </div>
              </motion.div>
            </motion.div>

            {/* ── Colonne droite : formulaire ── */}
            <div style={{ background: '#FFFFFF', border: '0.5px solid #E5E7EB', borderTop: `3px solid ${C.accent}`, borderRadius: 4, padding: isMobile ? '32px 20px' : '40px 40px' }}>
              {demoSent ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div style={{ fontFamily: C.display, fontSize: 30, fontWeight: 800, textTransform: 'uppercase', color: C.ink, marginBottom: 12 }}>
                    Message recu <span style={{ color: C.accent }}>✓</span>
                  </div>
                  <p style={{ fontFamily: C.body, fontSize: 14, color: '#6B7280', lineHeight: 1.7 }}>
                    On te répond sous 24 h ouvrées.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleDemoSubmit} noValidate>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                    <FieldWrap label="Prénom *" focused={demoFocused === 'prenom'}>
                      <input type="text" required value={demoForm.prenom} style={DEMO_INPUT_ST}
                        onChange={e => setDemoForm({ ...demoForm, prenom: e.target.value })}
                        onFocus={() => setDemoFocused('prenom')} onBlur={() => setDemoFocused(null)} />
                    </FieldWrap>
                    <FieldWrap label="Nom *" focused={demoFocused === 'nom'}>
                      <input type="text" required value={demoForm.nom} style={DEMO_INPUT_ST}
                        onChange={e => setDemoForm({ ...demoForm, nom: e.target.value })}
                        onFocus={() => setDemoFocused('nom')} onBlur={() => setDemoFocused(null)} />
                    </FieldWrap>
                  </div>

                  <FieldWrap label="Email *" focused={demoFocused === 'email'}>
                    <input type="email" required value={demoForm.email} style={DEMO_INPUT_ST}
                      onChange={e => setDemoForm({ ...demoForm, email: e.target.value })}
                      onFocus={() => setDemoFocused('email')} onBlur={() => setDemoFocused(null)} />
                  </FieldWrap>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                    <FieldWrap label="Téléphone" focused={demoFocused === 'telephone'}>
                      <input type="tel" value={demoForm.telephone} style={DEMO_INPUT_ST}
                        onChange={e => setDemoForm({ ...demoForm, telephone: e.target.value })}
                        onFocus={() => setDemoFocused('telephone')} onBlur={() => setDemoFocused(null)} />
                    </FieldWrap>
                    <FieldWrap label="Club / Structure *" focused={demoFocused === 'club'}>
                      <input type="text" required value={demoForm.club} style={DEMO_INPUT_ST}
                        onChange={e => setDemoForm({ ...demoForm, club: e.target.value })}
                        onFocus={() => setDemoFocused('club')} onBlur={() => setDemoFocused(null)} />
                    </FieldWrap>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                    <FieldWrap label="Rôle *" focused={demoFocused === 'role'}>
                      <select required value={demoForm.role} style={{ ...DEMO_INPUT_ST, appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer', color: demoForm.role ? '#0A0A0A' : '#9CA3AF' }}
                        onChange={e => setDemoForm({ ...demoForm, role: e.target.value })}
                        onFocus={() => setDemoFocused('role')} onBlur={() => setDemoFocused(null)}>
                        <option value="" disabled style={{ color: '#888' }}>Choisir...</option>
                        {['Directeur Sportif', 'Entraîneur / Éducateur', 'Analyste vidéo', 'Autre'].map(o => (
                          <option key={o}>{o}</option>
                        ))}
                      </select>
                    </FieldWrap>
                    <FieldWrap label="Niveau *" focused={demoFocused === 'niveau'}>
                      <select required value={demoForm.niveau} style={{ ...DEMO_INPUT_ST, appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer', color: demoForm.niveau ? '#0A0A0A' : '#9CA3AF' }}
                        onChange={e => setDemoForm({ ...demoForm, niveau: e.target.value })}
                        onFocus={() => setDemoFocused('niveau')} onBlur={() => setDemoFocused(null)}>
                        <option value="" disabled style={{ color: '#888' }}>Choisir...</option>
                        {['National', 'Régional', 'Départemental'].map(o => (
                          <option key={o}>{o}</option>
                        ))}
                      </select>
                    </FieldWrap>
                  </div>

                  <FieldWrap label="Message" focused={demoFocused === 'message'}>
                    <textarea value={demoForm.message} rows={3} style={{ ...DEMO_INPUT_ST, resize: 'vertical', minHeight: 72 }}
                      onChange={e => setDemoForm({ ...demoForm, message: e.target.value })}
                      onFocus={() => setDemoFocused('message')} onBlur={() => setDemoFocused(null)} />
                  </FieldWrap>

                  <button
                    type="submit"
                    disabled={demoLoading}
                    style={{
                      width: '100%', background: '#0A0A0A', color: '#FFFFFF',
                      fontFamily: C.mono, fontSize: 13, fontWeight: 700,
                      border: 'none', borderRadius: 3,
                      padding: '0 24px', minHeight: 52,
                      cursor: demoLoading ? 'not-allowed' : 'pointer',
                      opacity: demoLoading ? 0.65 : 1,
                      letterSpacing: '0.03em', transition: 'opacity .15s',
                      marginTop: 4,
                    }}
                  >
                    {demoLoading ? 'Envoi en cours...' : '→ Envoyer'}
                  </button>
                  <p style={{ fontFamily: C.body, fontSize: 11, color: '#9CA3AF', textAlign: 'center', marginTop: 12 }}>
                    Tes informations restent confidentielles.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <LandingFooter lang={lang} />
    </div>
  )
}
